import { api } from "@/lib/apiClient";
import { isAxiosError } from "axios";

export type SalaryAdjustmentStatus = "pending" | "applied" | "cancelled";
export type SalaryAdjustmentType = "addition" | "deduction";

export type EmployeeSalaryAdjustmentRow = {
  id: string;
  employeeId: string;
  employeeName: string | null;
  periodYear: number;
  periodMonth: number;
  type: SalaryAdjustmentType;
  title: string;
  amount: string;
  reason: string | null;
  status: SalaryAdjustmentStatus;
  salaryId: string | null;
  appliedAt: string | null;
  createdAt: string;
  updatedAt: string;
};

export type SalaryAdjustmentFormMeta = {
  blockedPeriods: Array<{ year: number; month: number }>;
};

type PaginatedResponse = {
  data?: Record<string, unknown>[];
  meta?: { current_page?: number; last_page?: number; per_page?: number; total?: number };
};

function str(v: unknown): string {
  if (v == null || v === "") return "";
  return String(v);
}

function mapType(raw: unknown): SalaryAdjustmentType {
  return str(raw) === "deduction" ? "deduction" : "addition";
}

function mapStatus(raw: unknown): SalaryAdjustmentStatus {
  const s = str(raw);
  if (s === "applied") return "applied";
  if (s === "cancelled") return "cancelled";
  return "pending";
}

function mapRow(raw: Record<string, unknown>): EmployeeSalaryAdjustmentRow {
  return {
    id: str(raw.id),
    employeeId: str(raw.employee_id),
    employeeName: raw.employee_name != null ? str(raw.employee_name) : null,
    periodYear: Number(raw.period_year) || 0,
    periodMonth: Number(raw.period_month) || 0,
    type: mapType(raw.type),
    title: str(raw.title),
    amount: str(raw.amount),
    reason: raw.reason != null ? str(raw.reason) : null,
    status: mapStatus(raw.status),
    salaryId: raw.salary_id != null ? str(raw.salary_id) : null,
    appliedAt: raw.applied_at != null ? str(raw.applied_at) : null,
    createdAt: str(raw.created_at),
    updatedAt: str(raw.updated_at),
  };
}

export function salaryAdjustmentsApiErrorMessage(error: unknown): string {
  if (isAxiosError(error)) {
    const data = error.response?.data as { message?: string; errors?: Record<string, string[]> } | undefined;
    if (data?.errors) {
      const first = Object.values(data.errors).flat()[0];
      if (first) return first;
    }
    if (typeof data?.message === "string") return data.message;
  }
  if (error instanceof Error && error.message) return error.message;
  return "Something went wrong.";
}

export async function listAllSalaryAdjustments(params: {
  page?: number;
  perPage?: number;
}): Promise<{ rows: EmployeeSalaryAdjustmentRow[]; meta: { currentPage: number; lastPage: number; perPage: number; total: number } }> {
  const response = await api.get<PaginatedResponse>("/api/v1/salary-adjustments", {
    params: {
      page: params.page,
      per_page: params.perPage,
    },
  });
  const list = Array.isArray(response.data?.data) ? response.data.data : [];
  const meta = response.data?.meta ?? {};
  return {
    rows: list.map((r) => mapRow(r as Record<string, unknown>)),
    meta: {
      currentPage: meta.current_page ?? 1,
      lastPage: meta.last_page ?? 1,
      perPage: meta.per_page ?? params.perPage ?? 20,
      total: meta.total ?? list.length,
    },
  };
}

export async function fetchSalaryAdjustmentFormMeta(employeeId: string): Promise<SalaryAdjustmentFormMeta> {
  const response = await api.get<{ data?: Record<string, unknown> }>("/api/v1/salary-adjustments/form-meta", {
    params: { employee_id: employeeId },
  });
  const raw = response.data?.data ?? {};
  const blocked = Array.isArray(raw.blocked_periods) ? raw.blocked_periods : [];
  return {
    blockedPeriods: blocked.map((p) => ({
      year: Number((p as { year?: unknown }).year) || 0,
      month: Number((p as { month?: unknown }).month) || 0,
    })),
  };
}

export async function createSalaryAdjustments(params: {
  employeeId: string;
  lines: Array<{
    type: SalaryAdjustmentType;
    title: string;
    amount: number | string;
    periodYear: number;
    periodMonth: number;
    reason?: string | null;
  }>;
}): Promise<{ rows: EmployeeSalaryAdjustmentRow[]; message: string }> {
  const response = await api.post<{ message?: string; data?: Record<string, unknown>[] }>(
    `/api/v1/employees/${encodeURIComponent(params.employeeId)}/salary-adjustments`,
    {
      lines: params.lines.map((line) => ({
        type: line.type,
        title: line.title,
        amount: line.amount,
        period_year: line.periodYear,
        period_month: line.periodMonth,
        reason: line.reason?.trim() || null,
      })),
    },
  );
  const list = Array.isArray(response.data?.data) ? response.data.data : [];
  return {
    rows: list.map((r) => mapRow(r as Record<string, unknown>)),
    message: str(response.data?.message) || "Salary adjustment recorded.",
  };
}

export async function updateSalaryAdjustment(params: {
  id: string;
  employeeId: string;
  type: SalaryAdjustmentType;
  title: string;
  amount: number | string;
  periodYear: number;
  periodMonth: number;
  reason?: string | null;
}): Promise<{ row: EmployeeSalaryAdjustmentRow; message: string }> {
  const response = await api.put<{ message?: string; data?: Record<string, unknown> }>(
    `/api/v1/salary-adjustments/${encodeURIComponent(params.id)}`,
    {
      employee_id: Number(params.employeeId),
      type: params.type,
      title: params.title,
      amount: params.amount,
      period_year: params.periodYear,
      period_month: params.periodMonth,
      reason: params.reason?.trim() || null,
    },
  );
  const raw = (response.data?.data ?? {}) as Record<string, unknown>;
  return {
    row: mapRow(raw),
    message: str(response.data?.message) || "Salary adjustment updated.",
  };
}

export async function cancelSalaryAdjustment(id: string): Promise<{ message: string }> {
  const response = await api.post<{ message?: string }>(
    `/api/v1/salary-adjustments/${encodeURIComponent(id)}/cancel`,
  );
  return {
    message: str(response.data?.message) || "Salary adjustment cancelled.",
  };
}

export function periodKey(year: number, month: number): string {
  return `${year}-${month}`;
}

export function periodLabel(year: number, month: number, months: readonly { v: number; label: string }[]): string {
  const m = months.find((x) => x.v === month);
  return `${m?.label ?? month} ${year}`;
}
